import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { useRecoilValue } from "recoil";

import {
  ldIdentifyPendingState,
  ldSyncedContextKeyState,
  patientState,
} from "../../recoil/atoms";
import { ANONYMOUS_LD_CONTEXT_KEY, FeatureFlags } from "../launchDarklyUtils";

interface PhlebotomySalesforceFinderRollout {
  contextKey: string;
  isEnabled: boolean;
  isResolved: boolean;
}

/**
 * Evaluate the catalog-finder rollout only after LaunchDarkly has identified
 * this page's patient (or anonymous) context. The same stable context key is
 * sent to the backend so a percentage rollout cannot select different finder
 * implementations for one visit.
 */
export const usePhlebotomySalesforceFinderRollout =
  (): PhlebotomySalesforceFinderRollout => {
    const flags = useFlags();
    const ldClient = useLDClient();
    const patientInfo = useRecoilValue(patientState);
    const ldSyncedContextKey = useRecoilValue(ldSyncedContextKeyState);
    const isLdIdentifyPending = useRecoilValue(ldIdentifyPendingState);

    const contextKey = patientInfo?.auth0.user_id ?? ANONYMOUS_LD_CONTEXT_KEY;
    const activeLdContext = ldClient?.getContext();
    const activeLdContextKey =
      activeLdContext && "key" in activeLdContext
        ? activeLdContext.key
        : undefined;
    const isContextSynced =
      ldSyncedContextKey === contextKey ||
      (!isLdIdentifyPending && activeLdContextKey === contextKey);
    const isResolved =
      isContextSynced &&
      Object.prototype.hasOwnProperty.call(
        flags,
        FeatureFlags.PatientPhlebotomySalesforceFinder,
      );

    return {
      contextKey,
      isEnabled:
        isResolved &&
        flags[FeatureFlags.PatientPhlebotomySalesforceFinder] === true,
      isResolved,
    };
  };
